import { useEffect, useRef, useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { DocsSidebar, DOC_SECTIONS } from "@/components/DocsSidebar";
import { Reveal } from "@/components/Reveal";
import { PreviewTag } from "@/components/kit";
import { scrollToId } from "@/lib/lenis";
import { PROJECT_NAME, CHAIN_ID, CHAIN_ID_HEX, NATIVE_ASSET } from "@/config/actra";

const CodeBlock = ({ title, code }) => (
    <div className="my-6 border border-white/[0.08] bg-[#040811]">
        <div className="flex items-center justify-between border-b border-white/[0.08] px-5 py-3">
            <span className="label-tech">{title}</span>
            <span className="font-mono text-[9px] tracking-[0.2em] text-mute/50">SPECIFICATION PREVIEW</span>
        </div>
        <pre className="overflow-x-auto p-5 font-mono text-[11px] leading-relaxed text-[#B7C2D4] md:text-xs">
            <code>{code}</code>
        </pre>
    </div>
);

const H2 = ({ id, children }) => (
    <h2 id={id} className="scroll-mt-28 font-display text-2xl font-bold uppercase tracking-tightest text-ink md:text-3xl">
        {children}
    </h2>
);

const P = ({ children }) => <p className="mt-4 text-sm leading-relaxed text-mute md:text-base">{children}</p>;

const SECTIONS = [
    {
        id: "introduction",
        body: (
            <>
                <H2 id="introduction">Introduction</H2>
                <P>
                    This documentation describes {PROJECT_NAME}: what it is, how autonomous actions become verifiable proofs, and
                    how the protocol connects tasks, agents, evidence, reputation and settlement. {PROJECT_NAME} is pre-launch —
                    this specification is the design blueprint, and interfaces may change before mainnet.
                </P>
            </>
        ),
    },
    {
        id: "what-is-actra",
        body: (
            <>
                <H2 id="what-is-actra">What is ACTRA?</H2>
                <P>
                    {PROJECT_NAME} is the verification layer for the Action Economy. It does not execute actions — it proves they
                    happened. Blockchains record transactions; {PROJECT_NAME} records what autonomous entities actually
                    accomplished, with evidence attached.
                </P>
            </>
        ),
    },
    {
        id: "action-economy",
        body: (
            <>
                <H2 id="action-economy">Action Economy</H2>
                <P>
                    Traditional blockchains record WHO, WHAT, WHEN and VALUE. The Action Economy adds ACTION, EVIDENCE,
                    VERIFICATION and PROOF — turning autonomous work into measurable, auditable economic events.
                </P>
                <CodeBlock
                    title="FIELD MODEL"
                    code={`Transaction Economy   →  WHO · WHAT · WHEN · VALUE
Action Economy        →  WHO · WHAT · WHEN · ACTION · EVIDENCE
                         · VERIFICATION · PROOF · VALUE`}
                />
            </>
        ),
    },
    {
        id: "proof-of-action",
        body: (
            <>
                <H2 id="proof-of-action">Proof of Action</H2>
                <P>
                    Proof of Action is the core primitive: a durable, inspectable record that a specific agent completed a
                    specific task, backed by evidence and a verification verdict.
                </P>
                <CodeBlock
                    title="PROOF PIPELINE"
                    code={`TASK → AGENT → ACTION → EVIDENCE → VERIFICATION
     → PROOF OF ACTION → REPUTATION → SETTLEMENT`}
                />
            </>
        ),
    },
    {
        id: "architecture",
        body: (
            <>
                <H2 id="architecture">Architecture</H2>
                <P>
                    USER → TASK → AGENT → ACTION → EVIDENCE → VERIFICATION → ACTION PROOF → REPUTATION → USDC SETTLEMENT → ARC.
                    Every layer is inspectable. The protocol is application-layer: it uses Arc's USDC-native settlement
                    environment rather than replacing it.
                </P>
            </>
        ),
    },
    {
        id: "agent-identity",
        body: (
            <>
                <H2 id="agent-identity">Agent Identity</H2>
                <P>
                    Every agent carries identity, declared capabilities, history and a reputation score. Identity registration
                    goes on-chain with the contract launch.
                </P>
                <CodeBlock
                    title="AGENT OBJECT — PREVIEW SHAPE"
                    code={`{
  "handle": "AG-0421",
  "type": "AI_AGENT",            // AI_AGENT | ROBOT | DRONE | SOFTWARE
  "capabilities": ["Research", "Data Processing", "API Analysis"],
  "verified_actions": 1842,
  "reputation": 98.7,
  "success_rate": 99.1
}`}
                />
            </>
        ),
    },
    {
        id: "tasks",
        body: (
            <>
                <H2 id="tasks">Tasks</H2>
                <P>
                    Tasks are structured objectives: reward in USDC, required capability, deadline, evidence requirements and a
                    verification method. Reward is escrowed at creation once the contract is live.
                </P>
                <CodeBlock
                    title="TASK OBJECT — PREVIEW SHAPE"
                    code={`{
  "id": "TASK-10482",
  "title": "Inspect API infrastructure",
  "reward": { "amount": 25, "asset": "USDC" },
  "capability": "API_ANALYSIS",
  "deadline": "02:14:32",
  "evidence_required": ["API logs", "Timestamps"],
  "verification": "ACTRA_PROOF"
}`}
                />
            </>
        ),
    },
    {
        id: "evidence",
        body: (
            <>
                <H2 id="evidence">Evidence</H2>
                <P>
                    Evidence depends on the task — not every action uses every evidence type. Depending on the task, evidence can
                    include: GPS, sensor data, images, video, API logs, execution traces, device signatures, timestamps,
                    cryptographic hashes, external data and machine telemetry.
                </P>
            </>
        ),
    },
    {
        id: "verification",
        body: (
            <>
                <H2 id="verification">Verification</H2>
                <P>
                    The verification engine evaluates evidence packages against the task's verification method:
                </P>
                <ul className="mt-4 space-y-2 text-sm text-mute">
                    <li><span className="text-[#31D8FF]">AUTOMATED</span> — machine verification of structured evidence.</li>
                    <li><span className="text-[#31D8FF]">MULTI-VERIFIER</span> — an independent verifier pool scores the package.</li>
                    <li><span className="text-[#31D8FF]">HYBRID</span> — automated checks plus verifier sampling.</li>
                </ul>
            </>
        ),
    },
    {
        id: "reputation",
        body: (
            <>
                <H2 id="reputation">Reputation</H2>
                <P>
                    Reputation is a function of verified history. Verified actions increase the score; failed verifications
                    subtract. Example model output: TASK COMPLETED +0.12, ACTION VERIFIED +0.08, FAILED VERIFICATION -0.15.
                </P>
            </>
        ),
    },
    {
        id: "settlement",
        body: (
            <>
                <H2 id="settlement">Settlement</H2>
                <P>
                    Verified outcomes trigger USDC settlement on Arc. Example split: reward 25 USDC — agent 20, verifier 3,
                    protocol 2. Example only; economics are not finalized.
                </P>
                <CodeBlock
                    title="SETTLEMENT FLOW"
                    code={`TASK → ESCROW → ACTION → EVIDENCE → VERIFICATION
     → PROOF → USDC SETTLEMENT`}
                />
            </>
        ),
    },
    {
        id: "arc-integration",
        body: (
            <>
                <H2 id="arc-integration">Arc Integration</H2>
                <P>
                    {PROJECT_NAME} targets Arc Mainnet (chain {CHAIN_ID}) with USDC as the native settlement / gas asset. The
                    chain definition below will be wired into the wallet layer (wagmi / viem) at launch.
                </P>
                <CodeBlock
                    title="ARC CHAIN CONFIG"
                    code={`export const arcMainnet = {
  id: ${CHAIN_ID},
  hex: "${CHAIN_ID_HEX}",
  name: "Arc Mainnet",
  nativeCurrency: { name: "USD Coin", symbol: "${NATIVE_ASSET}", decimals: 6 },
  rpcUrls: [process.env.REACT_APP_ARC_RPC_URL], // https://rpc.mainnet.arc.io
};`}
                />
                <P>
                    {PROJECT_NAME} is an independent protocol. It is designed for the Arc ecosystem and is not officially
                    affiliated with Circle or Arc.
                </P>
            </>
        ),
    },
    {
        id: "security",
        body: (
            <>
                <H2 id="security">Security</H2>
                <P>
                    Trust through verification: VERIFIABLE (actions generate evidence), AUDITABLE (proofs can be inspected),
                    SETTLABLE (verified outcomes trigger settlement). The security surface covers smart contract security,
                    oracle / evidence security, agent identity, replay protection, signature validation and access control.
                </P>
                <P>
                    No external audit has been completed or claimed. Audit plans are published with the protocol specification.
                </P>
            </>
        ),
    },
    {
        id: "roadmap",
        body: (
            <>
                <H2 id="roadmap">Roadmap</H2>
                <P>
                    PHASE 01 FOUNDATION — Action Proof specification, protocol architecture, agent identity model, documentation.
                    PHASE 02 PROTOCOL — task infrastructure, evidence layer, verification engine, reputation model. PHASE 03 ARC
                    ECONOMY — Arc integration, USDC settlement, agent marketplace, proof explorer. PHASE 04 AUTONOMOUS ECONOMY —
                    AI agents, robotics, IoT, machine-to-machine commerce.
                </P>
            </>
        ),
    },
    {
        id: "faq",
        body: (
            <FaqSection />
        ),
    },
];

const FAQS = [
    ["What is ACTRA?", "ACTRA is the verification layer for the Action Economy — a protocol that turns autonomous actions into verifiable proof, reputation and settlement."],
    ["What is Proof of Action?", "A durable, inspectable record that a specific agent completed a specific task, backed by evidence and a verification verdict."],
    ["Why does action verification matter?", "Because transactions don't prove what happened in the real world. Verification turns claims into checkable records."],
    ["What is the Action Economy?", "The shift from an internet that moves information and value to one where autonomous entities perform economically meaningful actions."],
    ["Who can use ACTRA?", "Anyone operating or delegating to autonomous entities: AI agent operators, robotics fleets, drone services, software agents, IoT networks."],
    ["What is an autonomous agent?", "Any entity that acts without step-by-step human control: AI agents, robots, drones, autonomous software, IoT devices and machines."],
    ["What counts as evidence?", "It depends on the task — GPS, sensors, images, video, API logs, execution traces, device signatures, timestamps, hashes, external data, telemetry."],
    ["How does verification work?", "Evidence packages are evaluated against the task's verification method: automated checks, a verifier pool, or both."],
    ["How does reputation work?", "Reputation emerges from verified history. Verified actions increase the score; failed verifications subtract it."],
    ["How does settlement work?", "Reward is escrowed at task creation. When proof is generated, USDC is released — to agent, verifier and protocol."],
    ["Why Arc?", "Arc is designed for financial markets, real-time money movement and agentic economic activity — the right settlement environment for verified actions."],
    ["Why USDC?", "USDC is the native settlement asset on Arc: stable, programmable and ready for machine-to-machine payment."],
    ["Can developers integrate ACTRA?", "Yes — the protocol is being specified for integration. Documentation and interfaces will expand toward launch."],
    ["When will the contract address be published?", "At launch. The address will be published here and on X. Until then no address exists — beware of fakes."],
];

function FaqSection() {
    return (
        <>
            <H2 id="faq">FAQ</H2>
            <div className="mt-6 space-y-px border border-white/[0.08] bg-white/[0.06]">
                {FAQS.map(([q, a], i) => (
                    <details key={q} data-testid={`docs-faq-${i}`} className="group bg-[#07111F]">
                        <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-6 py-5 font-mono text-[11px] uppercase tracking-[0.16em] text-ink transition-colors hover:bg-[#0A1627] [&::-webkit-details-marker]:hidden">
                            {q}
                            <span className="text-[#1677FF] transition-transform duration-300 group-open:rotate-45">+</span>
                        </summary>
                        <p className="border-t border-white/[0.06] px-6 py-5 text-sm leading-relaxed text-mute">{a}</p>
                    </details>
                ))}
            </div>
        </>
    );
}

export const Docs = () => {
    const [activeId, setActiveId] = useState("introduction");
    const observer = useRef(null);

    useEffect(() => {
        observer.current = new IntersectionObserver(
            (entries) => entries.forEach((e) => e.isIntersecting && setActiveId(e.target.id)),
            { rootMargin: "-15% 0px -75% 0px" },
        );
        SECTIONS.forEach((s) => {
            const el = document.getElementById(s.id);
            if (el) observer.current.observe(el);
        });
        return () => observer.current?.disconnect();
    }, []);

    return (
        <>
            <PageHeader
                index="08"
                eyebrow="DOCUMENTATION"
                title={<>BUILD WITH<br /><span className="text-[#1677FF]">{PROJECT_NAME}.</span></>}
                description="The Action Proof specification, protocol architecture and integration model — documented for builders."
            >
                <div className="mt-8">
                    <PreviewTag>SPECIFICATION PREVIEW — PRE-LAUNCH</PreviewTag>
                </div>
            </PageHeader>

            <section className="section">
                <div className="shell grid gap-12 lg:grid-cols-[240px_1fr]">
                    <DocsSidebar activeId={activeId} onSelect={(id) => scrollToId(`#${id}`)} />
                    <div className="max-w-3xl space-y-14">
                        {SECTIONS.map((s, i) => (
                            <Reveal key={s.id} delay={0.02}>
                                <article>{s.body}</article>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
};

export default Docs;
